import { type FC, type ReactNode, useCallback, useMemo } from "react";
import CustomNumberFormat, { type NumberFormatEvent } from "../../../../common/components/CustomNumberFormat";
import { type LinearCstic } from "../../models";
import {
  CSTIC_PART_MAX_SIZE_NAME,
  CSTIC_PART_MIN_SIZE_NAME,
  GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME,
  GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_HIGH_CSTIC_NAME,
  GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_LOW_CSTIC_NAME,
} from "../../constants";
import {
  flushPendingInputChanges,
  refreshGuidewayPieceLen,
  updateGuidewayPieceLen,
  updateSubstepAdditionalData,
} from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { getCsticNumberVal } from "../../utils";
import { NumberFormatter as NF } from "../../../../utils/NumberFormatter";
import classNames from "classnames";
import { getSubstepStep } from "../../redux/selectors";
import { type SubstepType } from "../../dtos";
import { Card } from "../Card";
import { useTranslation, type WithTranslation } from "react-i18next";
import { getGuidewayPieceDirtyParamName } from "~/Configurators/common/railLengthUtils";
import debounce from "lodash/debounce";

import { isNonNull } from "~/utils/reducers";
import { KmatType } from "~/Configurators/Linear/getKmatType";

interface Props {
  cstics: Dict<LinearCstic>;
  pieceIndex: number;
  kmatIndex: number;
  disabled?: boolean;
  isSecondLast?: boolean;
  isLast?: boolean;
  substep: SubstepType;
  makeMinMaxLabelFormatter: (t: WithTranslation["t"], min: number | null, max: number | null) => ReactNode;
}

export const PieceLengthInput: FC<Props> = ({
  cstics,
  pieceIndex,
  kmatIndex,
  disabled,
  substep,
  makeMinMaxLabelFormatter,
  isLast,
}) => {
  const dispatch = useDispatch();

  const length = getPieceLength(cstics, pieceIndex);
  const substepObj = useSelector(getSubstepStep(substep))!;
  const isDirty = substepObj.additionalData[getGuidewayPieceDirtyParamName(kmatIndex, pieceIndex + 1)] ?? false;
  const setDirty = useCallback(
    debounce((isDirty: boolean) => {
      dispatch(
        updateSubstepAdditionalData(
          [
            {
              type: KmatType.GUIDEWAY,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value: isDirty as any,
              index: kmatIndex,
              key: GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(pieceIndex + 1),
            },
          ].filter(isNonNull),
          substep,
        ),
      );
      dispatch(refreshGuidewayPieceLen(kmatIndex, pieceIndex, substep, true));
    }, 500),
    [],
  );

  const updateVal = useCallback(
    (e: NumberFormatEvent) => {
      dispatch(updateGuidewayPieceLen(kmatIndex, pieceIndex, substep, e.floatValue));
      setDirty(true);
    },
    [pieceIndex, kmatIndex, dispatch],
  );

  const { t } = useTranslation();

  let canShowSuggestion;
  if (pieceIndex === 0) {
    canShowSuggestion = true;
  } else if (isLast) {
    canShowSuggestion = false;
  } else {
    canShowSuggestion = new Array(pieceIndex).fill(1).every((_, index) => {
      const suggestionLowCstic = cstics[GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_LOW_CSTIC_NAME(index + 1)];
      const suggestionLow = getCsticNumberVal(suggestionLowCstic);

      const suggestionHighCstic = cstics[GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_HIGH_CSTIC_NAME(index + 1)];
      const suggestionHigh = getCsticNumberVal(suggestionHighCstic);
      return !suggestionLow && !suggestionHigh;
    });
  }

  const minLength = getCsticNumberVal(cstics[CSTIC_PART_MIN_SIZE_NAME]);
  const maxLength = getCsticNumberVal(cstics[CSTIC_PART_MAX_SIZE_NAME]);

  const suggestionLowCstic = cstics[GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_LOW_CSTIC_NAME(pieceIndex + 1)];
  const suggestionLow = getCsticNumberVal(suggestionLowCstic);

  const suggestionHighCstic = cstics[GET_GUIDEWAY_PIECE_SIZE_SUGGESTION_HIGH_CSTIC_NAME(pieceIndex + 1)];
  const suggestionHigh = getCsticNumberVal(suggestionHighCstic);

  const pieceHelpLabel = makeMinMaxLabelFormatter(t, minLength, maxLength);

  const hasAtLeastOneSuggestion = [suggestionLow, suggestionHigh].filter(Boolean).length > 0;

  // TODO: This validation should be in state, so we can access it in next step
  let isInvalid = false;
  if (hasAtLeastOneSuggestion) {
    isInvalid = true;
  } else if (length !== null) {
    if (isDirty || disabled) {
      isInvalid = !!((minLength && length < minLength) || (maxLength && length > maxLength));
    }
  } else if (!length && isDirty) {
    isInvalid = true;
  }

  const { setLow, setHigh } = useMemo(
    () => ({
      setLow: () => {
        dispatch(updateGuidewayPieceLen(kmatIndex, pieceIndex, substep, suggestionLow || undefined, true));
        setDirty(true);
        setDirty.flush();
      },
      setHigh: () => {
        dispatch(updateGuidewayPieceLen(kmatIndex, pieceIndex, substep, suggestionHigh || undefined, true));
        setDirty(true);
        setDirty.flush();
      },
    }),
    [dispatch, suggestionLow, suggestionHigh],
  );

  return (
    <div className={"scha__lc__piece-len-container"}>
      <CustomNumberFormat
        value={length}
        handleState={updateVal}
        label={t("page.linear.guidewaySet.pieceLength.title", {
          index: pieceIndex + 1,
        })}
        error={isInvalid || false}
        helperText={<span>{pieceHelpLabel}</span>}
        decimalScale={2}
        disabled={disabled}
        onBlur={() => {
          flushPendingInputChanges();
          setDirty(true);
          setDirty.flush();
        }}
      />
      {canShowSuggestion && hasAtLeastOneSuggestion && (
        <Card variant={"square"} className="scha__lc__piece-len-container__suggestion-list" disableHover={true}>
          <p className={"pt-2 pb-1 px-3 f-label-color"}>{t("page.linear.guidewaySet.pieceLength.gridError")}</p>
          {suggestionLow && (
            <SuggestionEntry onClick={setLow}>{NF.getDefaultFormatter().format(suggestionLow)}</SuggestionEntry>
          )}
          {suggestionHigh && (
            <SuggestionEntry onClick={setHigh}>{NF.getDefaultFormatter().format(suggestionHigh)}</SuggestionEntry>
          )}
        </Card>
      )}
    </div>
  );
};

const SuggestionEntry: FC<JSX.IntrinsicElements["div"]> = ({ className, ...props }) => (
  <div {...props} className={classNames(className, "scha__lc__piece-len-container__suggestion-item")} />
);

const getPieceLength = (cstics: Dict<LinearCstic>, index: number) => {
  const csticName = GET_GUIDEWAY_PIECE_SIZE_CSTIC_NAME(index + 1);
  const cstic = cstics[csticName];
  if (cstic && cstic.value !== undefined) {
    return +cstic.value;
  }
  return null;
};
